import React from 'react'
import styled from 'styled-components'
import Tooltip from '@mui/material/Tooltip'
import Avatar from 'components/Avatar'
import { maskEmail } from 'utils/string'

const RING = { assignee: '#ffffff', reviewer: '#7c3aed', external: '#f59e0b' }
const ROLE_LABEL = { assignee: '', reviewer: ' · Approver', external: ' · External' }

const externalPerson = (e, i) => {
  const parts = (e.name || '').trim().split(' ').filter(Boolean)
  return {
    key: `e-${e.id || i}`,
    role: 'external',
    src: null,
    email: e.email,
    firstName: parts[0] || e.name,
    lastName: parts.slice(1).join(' '),
    name: e.name || maskEmail(e.email)
  }
}

const buildPeople = (assignees = [], approvers = [], externalAssignees = []) => [
  ...(assignees || []).map(a => ({
    key: `a-${a.id}`, role: 'assignee', src: a.avatar,
    firstName: a.firstName, lastName: a.lastName, email: a.email,
    name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || maskEmail(a.email)
  })),
  ...(approvers || []).map(a => ({
    key: `r-${a.id}`, role: 'reviewer', src: a.avatar,
    firstName: a.firstName, lastName: a.lastName, email: a.email,
    name: `${a.firstName || ''} ${a.lastName || ''}`.trim() || maskEmail(a.email)
  })),
  ...(externalAssignees || []).map(externalPerson)
]

const AssigneeAvatars = ({
  assignees,
  approvers,
  externalAssignees,
  size = 24,
  max = 5,
  className
}) => {
  const people = buildPeople(assignees, approvers, externalAssignees)
  if (people.length === 0) return null

  const shown = people.slice(0, max)
  const overflow = people.length - shown.length

  const peopleList = (
    <PeopleList>
      {people.map(p => (
        <PeopleRow key={p.key}>
          <RoleDot $ring={RING[p.role]} />
          <PeopleName>{p.name}{ROLE_LABEL[p.role]}</PeopleName>
        </PeopleRow>
      ))}
    </PeopleList>
  )

  return (
    <Stack className={className}>
      {shown.map((p, i) => (
        <Tooltip key={p.key} title={`${p.name}${ROLE_LABEL[p.role]}`} arrow placement="top">
          <Wrap $ring={RING[p.role]} $index={i}>
            <Avatar src={p.src} firstName={p.firstName} lastName={p.lastName} email={p.email} size={size} />
          </Wrap>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <Tooltip title={peopleList} arrow placement="top">
          <More $size={size}>+{overflow}</More>
        </Tooltip>
      )}
    </Stack>
  )
}

export default AssigneeAvatars

const Stack = styled.div`
  display: flex;
  align-items: center;
`

const Wrap = styled.div`
  display: flex;
  border-radius: 50%;
  background: white;
  border: 2px solid ${({ $ring }) => $ring};
  margin-left: ${({ $index }) => ($index > 0 ? '-6px' : '0')};
  box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.06);
`

const More = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: #e6e6e6;
  color: #4b5563;
  font-size: ${({ $size }) => Math.max(8, Math.round($size * 0.38))}px;
  font-weight: 700;
  margin-left: -6px;
  border: 2px solid white;
  cursor: default;
`

const PeopleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 2px 0;
  max-height: 280px;
  overflow-y: auto;
`

const PeopleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  line-height: 1.35;
`

const RoleDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $ring }) => ($ring === '#ffffff' ? '#33becc' : $ring)};
  border: 1px solid rgba(255, 255, 255, 0.5);
`

const PeopleName = styled.span`
  white-space: nowrap;
`
